/**
 * TokenCard — Displays a single token in the discover grid.
 *
 * Shows logo, name, symbol, category badge, price, 24h change %,
 * and verification badge. Entire card is clickable to navigate to
 * the token detail page.
 *
 * Story 3.4 — Task 2
 */

import type { TokenListItem } from "@repo/shared-types";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";

import { cardHover, staggerItem } from "../../lib/animations";
import { resolveApiAssetUrl } from "../../lib/api";
import { cn } from "../../lib/cn";
import { ShareButton } from "../shared/ShareButton";

type TokenCardProps = {
	token: TokenListItem;
};

const categoryColors: Record<string, string> = {
	AI: "bg-pastel-blue/60 text-blue-700",
	DeFi: "bg-pastel-green/60 text-green-700",
	Gaming: "bg-pastel-purple/60 text-purple-700",
	Meme: "bg-pastel-peach/60 text-orange-700",
	Creator: "bg-pastel-pink/60 text-pink-700",
	Social: "bg-pastel-mint/60 text-teal-700",
	Infrastructure: "bg-pastel-light-gray text-pastel-gray",
	Other: "bg-pastel-light-gray text-pastel-gray",
};

function formatPrice(price: number | null): string {
	if (price === null) return "--";
	if (price >= 1) return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
	if (price >= 0.01) return `$${price.toFixed(4)}`;
	return `$${price.toFixed(6)}`;
}

function formatPriceChange(change: number | null): { text: string; className: string } {
	if (change === null) return { text: "--", className: "text-pastel-gray" };
	if (change > 0) return { text: `+${change.toFixed(1)}%`, className: "text-green-600" };
	if (change < 0) return { text: `${change.toFixed(1)}%`, className: "text-red-500" };
	return { text: "0.0%", className: "text-pastel-gray" };
}

function VerificationBadge({ status }: { status: string }) {
	if (status === "verified") {
		return (
			<span
				className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-600"
				title="Verified"
				aria-label="Verified token"
			>
				<svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
					<path
						fillRule="evenodd"
						d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
						clipRule="evenodd"
					/>
				</svg>
			</span>
		);
	}
	if (status === "flagged") {
		return (
			<span
				className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-yellow-100 text-yellow-600"
				title="Warning"
				aria-label="Flagged token"
			>
				<svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
					<path
						fillRule="evenodd"
						d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
						clipRule="evenodd"
					/>
				</svg>
			</span>
		);
	}
	return null;
}

export function TokenCard({ token }: TokenCardProps) {
	const priceChange = formatPriceChange(token.priceChange24h);
	const categoryStyle = categoryColors[token.category] ?? categoryColors.Other;

	const shareUrl = `${window.location.origin}/token/${token.id}`;

	return (
		<motion.div variants={staggerItem}>
			<Link to="/token/$tokenId" params={{ tokenId: token.id }}>
				<motion.div
					variants={cardHover}
					initial="rest"
					whileHover="hover"
					className="group relative rounded-xl border border-pastel-border bg-white p-4 cursor-pointer transition-colors hover:border-pastel-vibrant-blue/30"
				>
					<div className="absolute top-2 right-2 z-10 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
						<ShareButton url={shareUrl} />
					</div>
					<div className="flex items-start justify-between mb-3">
						<div className="flex items-center gap-2.5 min-w-0">
							{token.logoUrl ? (
								<img
									src={resolveApiAssetUrl(token.logoUrl) ?? undefined}
									alt={`${token.name} logo`}
									className="h-10 w-10 rounded-full object-cover flex-shrink-0"
								/>
							) : (
								<div className="h-10 w-10 rounded-full bg-pastel-light-gray flex items-center justify-center flex-shrink-0">
									<span className="text-sm font-bold text-pastel-gray">
										{token.symbol.slice(0, 2)}
									</span>
								</div>
							)}
							<div className="min-w-0">
								<div className="flex items-center gap-1.5">
									<h3 className="text-sm font-semibold text-pastel-black truncate">{token.name}</h3>
									<VerificationBadge status={token.verificationStatus} />
								</div>
								<p className="text-xs text-pastel-gray">{token.symbol}</p>
							</div>
						</div>
					</div>

					<div className="flex items-center justify-between mb-2">
						<span className="text-lg font-bold text-pastel-black">{formatPrice(token.price)}</span>
						<span className={cn("text-sm font-medium", priceChange.className)}>
							{priceChange.text}
						</span>
					</div>

					<div className="flex items-center justify-between">
						<span
							className={cn(
								"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
								categoryStyle
							)}
						>
							{token.category}
						</span>
						{token.volume24h !== null && (
							<span className="text-xs text-pastel-gray">
								Vol $
								{token.volume24h >= 1000
									? `${(token.volume24h / 1000).toFixed(1)}K`
									: token.volume24h.toFixed(0)}
							</span>
						)}
					</div>
				</motion.div>
			</Link>
		</motion.div>
	);
}
